import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — no furniture delegated act exists yet; the ESPR
 * working plan 2025-2030 puts furniture in the ~2028 wave. Modeled on the
 * ESPR Annex I information-requirement pattern, EU Ecolabel furniture
 * criteria (wood certification, formaldehyde/VOC limits, REACH) and EN
 * furniture durability norms. Field ids are STABLE (persisted in passport
 * versions); the list will be trued up when the delegated act lands by
 * adding fields / flipping `required` and `tier`, never by renaming ids.
 *
 * Tier logic (EN 18239 layered access): consumer-relevant facts are public;
 * supply-chain detail (wood origin, certificate numbers, chemical detail,
 * dismantling guidance for recyclers) is restricted to authenticated roles.
 */
export const furnitureV1: PassportSchemaDef = {
  id: "furniture-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Mobilya Ürün Pasaportu",
    en: "Furniture Product Passport",
    de: "Möbel-Produktpass",
  },
  // Draft vocabulary mirroring textile-v1: schema.org + GS1 plus an own
  // namespace until a delegated act fixes an official context.
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/furniture/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short consumer-facing description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.furnitureType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "seating",
            "table",
            "storage",
            "bed",
            "mattress",
            "kitchen-furniture",
            "office-furniture",
            "outdoor-furniture",
            "other",
          ],
          labels: { tr: "Mobilya türü", en: "Furniture type", de: "Möbeltyp" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.model",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Model referansı", en: "Model reference", de: "Modellreferenz" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "materials",
      labels: { tr: "Malzemeler", en: "Materials", de: "Materialien" },
      fields: [
        {
          id: "materials.materialSummary",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ana malzeme bileşimi",
            en: "Main material composition",
            de: "Hauptmaterialzusammensetzung",
          },
          description: 'Main materials by weight share, e.g. "70% oak / 20% steel / 10% PU foam".',
          highlight: true,
        },
        {
          id: "materials.woodSpecies",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ahşap türü", en: "Wood species", de: "Holzart" },
          description: "Common and/or botanical names of wood species used.",
        },
        {
          id: "materials.woodOrigin",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Ahşap menşei", en: "Wood origin", de: "Holzherkunft" },
          description: "Harvest country/region — EUDR-relevant supply-chain detail.",
        },
        {
          id: "materials.woodCertification",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["fsc", "pefc", "other", "none"],
          labels: { tr: "Ahşap sertifikası", en: "Wood certification", de: "Holzzertifizierung" },
        },
        {
          id: "materials.woodCertificateNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Ahşap sertifika numarası",
            en: "Wood certificate number",
            de: "Holzzertifikatsnummer",
          },
        },
        {
          id: "materials.metalTypes",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Metal türleri", en: "Metal types", de: "Metallarten" },
          description: "E.g. powder-coated steel, aluminium.",
        },
        {
          id: "materials.textileComposition",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Tekstil/döşeme bileşimi",
            en: "Textile & upholstery composition",
            de: "Textil-/Polsterzusammensetzung",
          },
          description: "Fiber composition of covers and upholstery, percentages by weight.",
        },
        {
          id: "materials.foamType",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Köpük/dolgu türü", en: "Foam & filling type", de: "Schaumstoff-/Füllmaterialtyp" },
        },
        {
          id: "materials.coatingsFinishes",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Kaplama ve cila", en: "Coatings & finishes", de: "Beschichtungen & Oberflächen" },
          description: "Lacquers, varnishes, oils, laminates.",
        },
        {
          id: "materials.recycledContentPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
          highlight: true,
        },
        {
          id: "materials.recycledContentPostConsumerPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tüketici sonrası geri dönüştürülmüş içerik",
            en: "Post-consumer recycled content",
            de: "Post-Consumer-Rezyklatanteil",
          },
        },
        {
          id: "materials.bioBasedContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Biyo-bazlı içerik", en: "Bio-based content", de: "Biobasierter Anteil" },
        },
      ],
    },
    {
      id: "chemicals",
      labels: {
        tr: "Kimyasallar ve Emisyonlar",
        en: "Chemicals & Emissions",
        de: "Chemikalien & Emissionen",
      },
      fields: [
        {
          id: "chemicals.formaldehydeClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["e1", "e05", "f4-star", "carb2", "no-added-formaldehyde", "not-applicable"],
          labels: {
            tr: "Formaldehit emisyon sınıfı",
            en: "Formaldehyde emission class",
            de: "Formaldehyd-Emissionsklasse",
          },
          description: "For wood-based panels; not-applicable when the product contains none.",
          highlight: true,
        },
        {
          id: "chemicals.vocEmissionClass",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["a-plus", "a", "b", "c", "not-tested"],
          labels: { tr: "VOC emisyon sınıfı", en: "VOC emission class", de: "VOC-Emissionsklasse" },
          description: "Indoor-air VOC emission class (French label scale A+ to C).",
        },
        {
          id: "chemicals.svhcPresent",
          type: "boolean",
          required: true,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          description: "REACH candidate-list substances of very high concern above 0.1% by weight.",
        },
        {
          id: "chemicals.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
          description: "Substance names/CAS numbers — expected when svhcPresent is true.",
        },
        {
          id: "chemicals.scipNumber",
          deprecatedBy: "compliance.scipNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "SCIP kayıt numarası", en: "SCIP number", de: "SCIP-Nummer" },
          description: "ECHA SCIP database reference for articles containing SVHC.",
        },
        {
          id: "chemicals.flameRetardantsPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Alev geciktirici içerir",
            en: "Contains flame retardants",
            de: "Enthält Flammschutzmittel",
          },
        },
        {
          id: "chemicals.flameRetardantsDetail",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Alev geciktirici detayı",
            en: "Flame retardant details",
            de: "Details zu Flammschutzmitteln",
          },
          description: "Substances used — relevant for recyclers and second-hand processing.",
        },
      ],
    },
    {
      id: "circularity",
      labels: {
        tr: "Dayanıklılık ve Döngüsellik",
        en: "Durability & Circularity",
        de: "Haltbarkeit & Kreislauffähigkeit",
      },
      fields: [
        {
          id: "circularity.expectedLifetimeYears",
          type: "number",
          required: true,
          tier: "public",
          unit: "years",
          labels: { tr: "Beklenen kullanım ömrü", en: "Expected lifetime", de: "Erwartete Lebensdauer" },
          highlight: true,
        },
        {
          id: "circularity.warrantyYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: { tr: "Garanti süresi", en: "Warranty period", de: "Garantiedauer" },
        },
        {
          id: "circularity.durabilityTestStandard",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Dayanıklılık test standardı",
            en: "Durability test standard",
            de: "Haltbarkeits-Prüfnorm",
          },
          description: "E.g. EN 1728 seating strength, EN 16139 — evidence for the lifetime claim.",
        },
        {
          id: "circularity.repairabilityScore",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["A", "B", "C", "D", "E"],
          labels: { tr: "Onarılabilirlik sınıfı", en: "Repairability score", de: "Reparierbarkeitsklasse" },
        },
        {
          id: "circularity.sparePartsAvailable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Yedek parça mevcut", en: "Spare parts available", de: "Ersatzteile verfügbar" },
          description: "Legs, hinges, covers, fittings etc.",
        },
        {
          id: "circularity.sparePartsYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: {
            tr: "Yedek parça bulunabilirlik süresi",
            en: "Spare parts availability period",
            de: "Ersatzteil-Verfügbarkeitsdauer",
          },
        },
        {
          id: "circularity.repairInstructionsUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Onarım talimatları", en: "Repair instructions", de: "Reparaturanleitung" },
        },
        {
          id: "circularity.disassemblyInfo",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Söküm/ayrıştırma bilgisi",
            en: "Disassembly information",
            de: "Demontageinformationen",
          },
          description: "For recyclers: how to separate wood, metal, foam and textile fractions.",
        },
        {
          id: "circularity.disassemblyInstructionsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Söküm talimatları bağlantısı",
            en: "Disassembly instructions link",
            de: "Link zur Demontageanleitung",
          },
        },
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
      ],
    },
    {
      id: "endOfLife",
      labels: { tr: "Ömür Sonu", en: "End-of-Life", de: "Lebensende" },
      fields: [
        {
          id: "endOfLife.collectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu toplama bilgisi",
            en: "End-of-life collection information",
            de: "Informationen zur Altmöbelsammlung",
          },
          description: "Where/how to return or dispose of the product (bulky waste, take-back).",
        },
        {
          id: "endOfLife.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
        {
          id: "endOfLife.reusePotential",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yeniden kullanım potansiyeli",
            en: "Reuse potential",
            de: "Wiederverwendungspotenzial",
          },
          description: "Suitability for refurbishment/second-hand channels.",
        },
        {
          id: "endOfLife.materialSeparationGuidance",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Malzeme ayrıştırma rehberi",
            en: "Material separation guidance",
            de: "Anleitung zur Materialtrennung",
          },
          description: "For waste operators: fraction-by-fraction separation guidance.",
        },
        {
          id: "endOfLife.hazardousComponentsRemoval",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tehlikeli bileşen çıkarma bilgisi",
            en: "Hazardous component removal",
            de: "Entfernung gefährlicher Komponenten",
          },
          description: "E.g. treated wood parts or flame-retardant foams needing separate handling.",
        },
      ],
    },
  ],
};
